import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {SmagApiService} from '../../../api/smag-api.service';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {HttpErrorResponse} from '@angular/common/http';
import {DialogData} from '../ec-measures.component';

const MAX_FILE_MB = 4;
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const DIALOG_WIDTH = 700;

interface Template {
    name: string;
    file: File;
}


@Component({
    selector: 'app-gse-upload',
    templateUrl: './gse-upload.component.html',
    styleUrls: ['./gse-upload.component.scss']
})
export class GseUploadComponent implements OnInit {

    XLSX_MIME_TYPE: string = XLSX_MIME_TYPE;
    MAX_FILE_MB: number = MAX_FILE_MB;
    templateChosen: boolean;
    template: Template;
    @Input() resource: DialogData;
    @Output() dataUploaded: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('gseErrors') private gseErrorsDialog: TemplateRef<any>;

    constructor(private readonly dialogService: DialogService,
                private readonly apiService: SmagApiService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService) {
    }

    ngOnInit(): void {
        this.template = {name: null, file: null};
        this.templateChosen = false;
    }

    sendIt() {
        const spinner = this.openSpinner('We\'re uploading your measures.\n You\'ll see them shortly.');
        this.apiService.uploadGSEMeasures(this.resource.resourceId,
            {content: this.template.file, filename: this.template.name})
            .then(gseResponse => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('File correctly uploaded: consolidated data will arrive shortly'),
                });
                return gseResponse;
            })
            .then(gseResponse => {
                if (gseResponse.errors?.length || gseResponse.missing_pods?.length) {
                    spinner.context.next({
                        loading: false,
                        loadingMessage: null,
                        abort: true
                    });
                    this.dialogService.open<any>({
                        template: this.gseErrorsDialog,
                        context: {gseResponse},
                        title: null,
                    }).closed.then(() => this.dataUploaded.emit());
                } else {
                    this.dataUploaded.emit();
                }
            })
            .catch(error => {
                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate('Error during document upload'),
                    abort: true
                });
                if (error instanceof HttpErrorResponse && (error as HttpErrorResponse).status === 413) {
                    this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', this.template.name, MAX_FILE_MB));
                } else {
                    this.msgBoxService.error(this.i18n.translate('Un unexpected error occurred while uploading the file'));
                }
            });

    }

    onClickUploadTemplate($event: Event) {
        const tg = $event.target as HTMLInputElement;
        if (tg.files && tg.files.length) {
            const file: File = tg.files[0];
            if (this.validateFile(file)) {
                this.template = {name: file.name, file};
                this.templateChosen = true;
            }

        }
    }

    private validateFile(file: File): boolean {
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        if (file.type !== XLSX_MIME_TYPE) {
            this.msgBoxService.error(this.i18n.translate('File {0} is not an XLSX file', file.name));
            return false;
        }
        return true;
    }

    onClickTrashTemplate(fileSelect: HTMLInputElement) {
        fileSelect.value = null; // allows file re-selection
        this.template = {name: null, file: null};
        this.templateChosen = false;
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: false
        });
    }

}
